/**
 * Página de listagem de produtos para previsão
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Box, Fab, Badge, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ShoppingCart, ViewModule, ViewList } from '@mui/icons-material';
import { Layout, Loading, ErrorMessage, SearchBar, EmptyState, PageHeader } from '@components';
import { ProdutoCard } from '@components/produto/ProdutoCard';
import { ProdutoCardItem } from '@components/produto/ProdutoCardItem';
import { CarrinhoProvider, useCarrinhoContext } from '@contexts';
import { useFetch, useFilter, useDebounce } from '@hooks';
import { produtoController, classificacaoController } from '@controllers';
import { Inventory } from '@mui/icons-material';
import { Produto } from '@types';
import { CacheUtil } from '@utils';
import { PainelPrevisao } from '@components';
import theme from '@theme';

type ViewMode = 'grid' | 'list';
type OrderBy = 'nome-asc' | 'nome-desc' | 'preco-asc' | 'preco-desc';

// Componente otimizado para cada item da lista - Grid
const ProdutoItemGrid: React.FC<{
  produto: Produto;
  quantidade: number;
  onUpdateQuantidade: (codigo: string, qtd: number) => void;
}> = React.memo(({ produto, quantidade, onUpdateQuantidade }) => {
  const handleUpdate = useCallback((qtd: number) => {
    onUpdateQuantidade(produto.codigo, qtd);
  }, [produto.codigo, onUpdateQuantidade]);

  return (
    <Grid item xs={6} sm={6} md={3}>
      <ProdutoCard
        produto={produto}
        tipo="previsao"
        quantidade={quantidade}
        disponivel={5000}
        onUpdateQuantidade={handleUpdate}
      />
    </Grid>
  );
}, (prevProps, nextProps) => {
  return prevProps.quantidade === nextProps.quantidade &&
         prevProps.produto.codigo === nextProps.produto.codigo;
});

ProdutoItemGrid.displayName = 'ProdutoItemGrid';

// Componente otimizado para cada item da lista - List
const ProdutoItemList: React.FC<{
  produto: Produto;
  quantidade: number;
  onUpdateQuantidade: (codigo: string, qtd: number) => void;
}> = React.memo(({ produto, quantidade, onUpdateQuantidade }) => {
  const handleUpdate = useCallback((qtd: number) => {
    onUpdateQuantidade(produto.codigo, qtd);
  }, [produto.codigo, onUpdateQuantidade]);

  return (
    <ProdutoCardItem
      produto={produto}
      tipo="previsao"
      quantidade={quantidade}
      disponivel={5000}
      onUpdateQuantidade={handleUpdate}
    />
  );
}, (prevProps, nextProps) => {
  return prevProps.quantidade === nextProps.quantidade &&
         prevProps.produto.codigo === nextProps.produto.codigo;
});

ProdutoItemList.displayName = 'ProdutoItemList';

const ListaProdutoPrevisaoContent: React.FC = () => {
  const { idClassificacao } = useParams<{ idClassificacao: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list'); // Começa em modo lista (compacto)
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [produtos, setProdutos] = useState<Produto[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { addItem, updateQuantity, getItemQuantity, totalItems } = useCarrinhoContext();

  // Carrega classificação
  const { data: classificacao } = useFetch(
    () =>
      idClassificacao
        ? classificacaoController.obterPorCodigo(idClassificacao)
        : Promise.resolve(null),
    { key: idClassificacao }
  );

  // Carrega produtos com cache
  useEffect(() => {
    const loadProdutos = async () => {
      if (!idClassificacao) {
        setProdutos([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Tenta carregar do cache primeiro (apenas se não houver busca)
        if (!debouncedSearch) {
          const cached = CacheUtil.loadProdutosCache('previsao', idClassificacao);
          if (cached) {
            console.log('Produtos carregados do cache');
            setProdutos(cached);
            setIsLoading(false);
            return;
          }
        }

        // Busca da API
        console.log('Carregando produtos da API...');
        const result = await produtoController.obterProdutos(idClassificacao, debouncedSearch);
        setProdutos(result);

        // Salva no cache apenas se não houver busca
        if (!debouncedSearch && result.length > 0) {
          CacheUtil.saveProdutosCache('previsao', idClassificacao, result);
          console.log('Produtos salvos no cache');
        }
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProdutos();
  }, [idClassificacao, debouncedSearch]);

  const refetch = useCallback(() => {
    // Invalida cache e recarrega
    if (idClassificacao) {
      CacheUtil.invalidateProdutosCache('previsao', idClassificacao);
      window.location.reload();
    }
  }, [idClassificacao]);

  // Filtra produtos
  const { filteredData } = useFilter(produtos || [], ['nome', 'codigo']);

  // Ordena produtos e remove duplicatas
  const sortedData = useMemo(() => {
    if (!filteredData) return [];

    // Remove duplicatas baseado no código do produto
    const uniqueProducts = Array.from(
      new Map(filteredData.map(produto => [produto.codigo, produto])).values()
    );

    const [field, direction] = orderBy.split('-') as ['nome' | 'preco', 'asc' | 'desc'];

    if (field === 'nome') {
      return produtoController.ordenarPorNome(uniqueProducts, direction);
    } else {
      return produtoController.ordenarPorPreco(uniqueProducts, direction);
    }
  }, [filteredData, orderBy]);

  const handleUpdateQuantidade = useCallback((codigoProduto: string, quantidade: number) => {
    if (quantidade === 0) {
      updateQuantity(codigoProduto, 0);
    } else if (getItemQuantity(codigoProduto) === 0) {
      // Precisa do produto completo para adicionar
      const produto = produtos?.find(p => p.codigo === codigoProduto);
      if (produto) {
        addItem(produto, quantidade);
      }
    } else {
      updateQuantity(codigoProduto, quantidade);
    }
  }, [addItem, updateQuantity, getItemQuantity, produtos]);

  if (isLoading) {
    return <Loading message="Carregando produtos..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Erro ao carregar produtos"
        message={error.message}
        onRetry={refetch}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={classificacao?.nome || 'Produtos'}
        subtitle={`${sortedData.length} produtos disponíveis`}
        showBackButton
        onBack={() => navigate('/classificacoes')}
      />

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: 1 }, order: { xs: 1, sm: 1 } }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar produto..."
          />
        </Box>

        <FormControl sx={{ minWidth: 200, display: { xs: 'none', md: 'flex' }, order: { sm: 2 } }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="preco-asc">Menor preço</MenuItem>
            <MenuItem value="preco-desc">Maior preço</MenuItem>
          </Select>
        </FormControl>

        {/* Toggle de visualização */}
        <Box sx={{ display: 'flex', gap: 0.5, order: { xs: 2, sm: 3 } }}>
          <Tooltip title="Visualização em Grade">
            <IconButton
              color={viewMode === 'grid' ? 'primary' : 'default'}
              onClick={() => setViewMode('grid')}
              size="large"
            >
              <ViewModule />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visualização em Lista">
            <IconButton
              color={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
              size="large"
            >
              <ViewList />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {sortedData.length === 0 ? (
        <EmptyState
          title="Nenhum produto encontrado"
          message={
            searchTerm ? 'Tente ajustar sua busca' : 'Não há produtos nesta categoria'
          }
          icon={<Inventory />}
        />
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {sortedData.map((produto) => (
            <ProdutoItemGrid
              key={produto.codigo}
              produto={produto}
              quantidade={getItemQuantity(produto.codigo)}
              onUpdateQuantidade={handleUpdateQuantidade}
            />
          ))}
        </Grid>
      ) : (
        <Box>
          {sortedData.map((produto) => (
            <ProdutoItemList
              key={produto.codigo}
              produto={produto}
              quantidade={getItemQuantity(produto.codigo)}
              onUpdateQuantidade={handleUpdateQuantidade}
            />
          ))}
        </Box>
      )}

      {/* FAB do Carrinho  */ }
      {totalItems > 0 && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            display: 'none'
          }}
          onClick={() => navigate('/carrinho')}
        >
          <Badge badgeContent={totalItems} color="error">
            <ShoppingCart />
          </Badge>
        </Fab>
      )}
    </>
  );
};

export const ListaProdutoPrevisaoPage: React.FC = () => {
  return (
    <Layout title="Previsão" showHeader showFooter footerType="previsao" showPainelPrevisao>
      <PainelPrevisao />
      <CarrinhoProvider tipo="previsao">
        <ListaProdutoPrevisaoContent />
      </CarrinhoProvider>
    </Layout>
  );
};

export default ListaProdutoPrevisaoPage;